import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import prepareAuthHeaders from "@utils/prepareAuthHeaders";
import { Photo } from "@models/photo";
import { PageResponse } from "@models/page-response";
import { SuccessResponse } from "@models/success-response";
import { albumApi } from "@store/albumApi";
import { PhotosProcessingStatus } from "@models/photos-processing-status";
import { characterApi } from "@store/characterApi";
import { accountSetupApi } from "@store/accountSetupApi";
import { planApi } from "@store/planApi";

const BASE_API_URL = process.env.REACT_APP_API_URL;

export interface PhotoUpload {
  file: File;
  patientId: string;
  fileName: string;
}

export interface PhotoSearchFilters {
  labelIds?: string[];
  characterIds?: string[];
  startDate?: Date;
  endDate?: Date;
  search?: string;
}

export interface UpdatePhotoPayload {
  photoId: string;
  placeId?: string;
  userDescription?: string;
}

export const photoApi = createApi({
  reducerPath: "photoApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_API_URL}/photos`,
    prepareHeaders: prepareAuthHeaders,
  }),
  tagTypes: ["Photo", "Album"],
  endpoints: (builder) => ({
    uploadPhoto: builder.mutation<Photo, PhotoUpload>({
      invalidatesTags: ["Photo"],
      query: ({ file, patientId, fileName }: PhotoUpload) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("patientId", patientId);
        formData.append("fileName", fileName);

        return {
          url: "",
          method: "POST",
          body: formData,
          formData: true,
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(characterApi.util.invalidateTags(["Character"]));
          dispatch(
            accountSetupApi.util.invalidateTags(["AccountSetupProgress"]),
          );
          dispatch(planApi.util.invalidateTags(["Subscription"]));
          dispatch(albumApi.util.invalidateTags(["Album", "Photo"]));
        } catch (e) {}
      },
    }),
    deletePhoto: builder.mutation<SuccessResponse, string>({
      invalidatesTags: ["Photo"],
      query: (id) => {
        return {
          url: id,
          method: "DELETE",
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(albumApi.util.invalidateTags(["Album", "Photo"]));
          dispatch(
            accountSetupApi.util.invalidateTags(["AccountSetupProgress"]),
          );
          dispatch(planApi.util.invalidateTags(["Subscription"]));
        } catch (e) {}
      },
    }),
    getPhotos: builder.query<
      PageResponse<Photo>,
      {
        patientId: string;
      } & PhotoSearchFilters
    >({
      providesTags: ["Photo"],
      query: (params) => {
        return {
          url: ``,
          method: "GET",
          params: {
            ...params,
            startDate: params.startDate?.toISOString(),
            endDate: params.endDate?.toISOString(),
            page: 1,
            pageSize: 150,
          },
        };
      },
    }),
    getPhoto: builder.query<Photo, string>({
      providesTags: ["Photo"],
      query: (id: string) => {
        return {
          url: id,
          method: "GET",
        };
      },
    }),
    updatePhoto: builder.mutation<Photo, UpdatePhotoPayload>({
      invalidatesTags: ["Photo"],
      query: ({ photoId, placeId, userDescription }) => {
        return {
          url: photoId,
          method: "PATCH",
          body: { placeId, userDescription },
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(albumApi.util.invalidateTags(["Album", "Photo"]));
        } catch (e) {}
      },
    }),
    deletePhotoLabel: builder.mutation<
      void,
      {
        labelId: string;
        photoId: string;
      }
    >({
      invalidatesTags: ["Photo"],
      query: ({ labelId, photoId }) => {
        return {
          url: `${photoId}/labels/${labelId}`,
          method: "DELETE",
        };
      },
    }),
    getProcessingStatus: builder.query<
      PhotosProcessingStatus,
      { patientId: string }
    >({
      query: (params) => {
        return {
          url: "status",
          method: "GET",
          params,
        };
      },
    }),
  }),
});

export const {
  useUploadPhotoMutation,
  useGetPhotosQuery,
  useDeletePhotoMutation,
  useGetPhotoQuery,
  useUpdatePhotoMutation,
  useDeletePhotoLabelMutation,
  useGetProcessingStatusQuery,
} = photoApi;
